import env from 'env'

import { type Config, VendorVideoPlayer } from '..'
import { load } from '../../../utils'

import {
  VIDEO_PLAYBACK_ENDED,
  VIDEO_PLAYBACK_ERROR,
  VIDEO_PLAYBACK_PAUSE,
  VIDEO_PLAYBACK_SEEK,
  VIDEO_PLAYBACK_START,
  VIDEO_PLAYBACK_TIME,
  VIDEO_PLAYER_CHAPTERS_TOGGLE,
  VIDEO_PLAYER_MOUNTED,
  VIDEO_PLAYER_UNMOUNTED
} from '../../../events/types/video.events'

import { EVENT_PLAYBACK_STATUS } from '../../../constants/enums'

type JWP = ReturnType<JWPlayerStatic>

declare global {
  interface Window {
    jwplayer: JWPlayerStatic
  }
}

const VENDOR_ID = 'QG6HrdTr'
const CHAPTER_ICON = `
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M5 5H3V7H5V5Z" fill="white"/>
  <path d="M8 7H21V5H8V7Z" fill="white"/>
  <path d="M8 13H21V11H8V13Z" fill="white"/>
  <path d="M8 19H21V17H8V19Z" fill="white"/>
  <path d="M3 11H5V13H3V11Z" fill="white"/>
  <path d="M5 17H3V19H5V17Z" fill="white"/>
</svg>`.trim()

export class NugsJWPlayer extends VendorVideoPlayer<string> {
  jwplayer?: JWP

  static get name() {
    return 'NugsJwplayerPlayer'
  }

  public get container(): HTMLElement {
    return this.jwplayer?.getContainer() as HTMLElement
  }

  public get element(): HTMLVideoElement {
    return this.container?.querySelector('video') as HTMLVideoElement
  }

  public get currentTime() {
    return this.jwplayer?.getPosition() ?? 0
  }

  public get isFullscreen() {
    return this.jwplayer?.getFullscreen() ?? false
  }

  constructor(config: Config, userId = '') {
    super(config, userId)
  }

  async load<JWPlayerStatic>() {
    if (this.jwplayer) return window.jwplayer as JWPlayerStatic
    return await load<JWPlayerStatic>(`https://cdn.jwplayer.com/libraries/${VENDOR_ID}.js`, 'jwplayer')
  }

  public async mount(el?: HTMLElement) {
    this.jwplayer = window.jwplayer(el)
  }

  async init(config = {}) {
    this.jwplayer?.setup({
      playlist: [
        {
          file: this.src,
          tracks: [
            {
              'file': this.getBlobChapters(),
              'kind': 'chapters'
            }
          ],
          sources: [],
          title: this.config?.meta?.title || '',
          image: this.config?.preview,
        }
      ],
      width: this.config.width ?? 640,
      height: this.config.height ?? 360,
      preload: 'auto',
      cast: env.CHROMECAST_APPLICATION_ID ? {
        appid: env.CHROMECAST_APPLICATION_ID,
      } : undefined,
      autostart: this.config.autoplay,
      displaytitle: false,
      displaydescription: false,
      ...config
    })

    if (this.chapters.length) {
      this.jwplayer?.addButton(CHAPTER_ICON as string, 'Chapters', () => {
        this.events.dispatchEvent(VIDEO_PLAYER_CHAPTERS_TOGGLE(this.chapters.data))
      }, 'chapters-list', 'chapters-list')
    }

    this.jwplayer?.on('ready', () => {
      this.video = this.element
      this.events.dispatchEvent(VIDEO_PLAYER_MOUNTED)
    })

    this.jwplayer?.on('play', () => this.events.dispatchEvent(VIDEO_PLAYBACK_START))
    this.jwplayer?.on('pause', () => this.events.dispatchEvent(VIDEO_PLAYBACK_PAUSE))
    this.jwplayer?.on('seek', event => {
      const position = event['currentTime' as never]

      this.chapters.track(position, event.offset, EVENT_PLAYBACK_STATUS.SKIPPED)
      this.events.dispatchEvent(VIDEO_PLAYBACK_SEEK(position, event.offset))
    })
    this.jwplayer?.on('complete', () => this.events.dispatchEvent(VIDEO_PLAYBACK_ENDED))
    this.jwplayer?.on('time', event => {
      const position = event['currentTime' as never]
      this.chapters.track(position, position, EVENT_PLAYBACK_STATUS.COMPLETED)
      this.events.dispatchEvent(VIDEO_PLAYBACK_TIME(
        position,
        this.element.played,
        event.duration
      ))
    })

    this.jwplayer?.on('error', () => this.events.dispatchEvent(VIDEO_PLAYBACK_ERROR))
  }

  public unmount() {
    this.events.dispatchEvent(VIDEO_PLAYER_UNMOUNTED)
    this.jwplayer?.remove()
    return Promise.resolve()
  }

  public play() {
    this.jwplayer?.play()
  }

  public pause() {
    this.jwplayer?.pause()
  }

  public stop() {
    this.jwplayer?.stop()
  }

  public seek(time: number) {
    this.jwplayer?.seek(time)
  }

  private getBlobChapters(): string {
    if (!this.chapters) {
      return ''
    }

    let result = ''
    const lastIndex = this.chapters.length - 1

    for (let index = 0; index <= lastIndex; index++) {
      result += `${index} \n `
      result += getChapterPositionFormatted(this.chapters.list[index].marker) + ' --> '
      result += index !== lastIndex ? getChapterPositionFormatted(this.chapters.list[index + 1].marker) : '00:00:00'
      result += '\n' + this.chapters.list[index].title + '\n\n'
    }

    return window.URL.createObjectURL(new Blob([result], { type: 'text/plain' }))
  }
}

function getChapterPositionFormatted(marker: number) : string {
  const hours = Math.floor(marker / 3600)
  const restChapterSeconds = marker - hours * 3600
  const minutes = Math.floor(restChapterSeconds / 60)
  const seconds = restChapterSeconds - minutes * 60

  let minutesStr = String(minutes)
  minutesStr = minutesStr.length <= 1 ? '0' + minutesStr : minutesStr

  let secondsStr = String(seconds)
  secondsStr = secondsStr.length <= 1 ? '0' + secondsStr : secondsStr

  let hoursStr = String(hours)
  hoursStr = hoursStr.length <= 1 ? '0' + hoursStr : hoursStr

  if (hours <= 0) {
    return minutesStr + ':' + secondsStr
  }

  return hoursStr + ':' + minutesStr + ':' + secondsStr
}
